import { flagshipProject } from '../data/projects';
import { SITE_CONFIG } from '../config/site';
import type { ResolvedTheme, StandaloneHtmlOptions } from '../types';
import indexCss from '../index.css?raw';
import appCss from '../App.css?raw';

/**
 * Generates the clean offline-ready modal dialog markup with zero external dependencies.
 */
export function getStandaloneModalHtml(siteConfig = SITE_CONFIG): string {
  const linkedinUrl = siteConfig.defaultLinkedinUrl;
  const linkedinVanity = siteConfig.defaultLinkedinVanity;
  const githubUrl = siteConfig.githubUrl;

  return `<div id="resume-modal" class="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="modal-title" style="display: none;" aria-hidden="true" data-testid="modal-overlay">
  <div class="modal-content">
    <div class="modal-header">
      <div class="modal-header-text">
        <div class="modal-eyebrow-row">
          <span class="modal-eyebrow">Verified Credentials Dispatch</span>
          <span class="modal-security-pill">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span>Verified Professional Profile &amp; Governance Compliant</span>
          </span>
        </div>
        <h3 id="modal-title" class="modal-title">Request Full Engineering Résumé</h3>
      </div>
      <button type="button" class="modal-close-btn" aria-label="Close modal">&times;</button>
    </div>

    <div class="modal-body">
      <p class="modal-intro">
        Client-side compiled executive résumé with privacy-safe redactions. Select a target format or connect via verified channels:
      </p>

      <section class="modal-export-section" aria-labelledby="export-section-title">
        <div class="modal-export-header">
          <h4 id="export-section-title" class="modal-section-title">Instant PDF Export</h4>
          <span class="modal-export-badge">Client-Side Vector Engine</span>
        </div>

        <div class="modal-export-toolbar">
          <div class="modal-select-grid">
            <div class="modal-select-wrapper">
              <label for="pdf-orientation-select" class="modal-select-label">Layout Orientation</label>
              <select id="pdf-orientation-select" class="modal-select" aria-label="Select PDF Orientation">
                <option value="portrait" selected>Portrait (A4 Executive CV)</option>
                <option value="landscape">Landscape (A4 Presentation)</option>
              </select>
            </div>

            <div class="modal-select-wrapper">
              <label for="pdf-theme-select" class="modal-select-label">Color Theme</label>
              <select id="pdf-theme-select" class="modal-select" aria-label="Select PDF Theme">
                <option value="light" selected>Light Theme (Print &amp; ATS)</option>
                <option value="dark">Dark Theme (Cyber Screen)</option>
              </select>
            </div>
          </div>

          <div class="modal-export-buttons">
            <button type="button" class="modal-action-btn primary export-trigger-btn" aria-label="Download Résumé PDF">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <div class="btn-text-block">
                <strong>Download Résumé PDF</strong>
                <span>A4 Executive CV • Light Theme</span>
              </div>
              <span class="modal-btn-badge">A4 • PORTRAIT</span>
            </button>

            <button type="button" class="modal-action-btn html-export-btn export-trigger-btn" aria-label="Download Standalone Offline HTML Portfolio">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="16 18 22 12 16 6" />
                <polyline points="8 6 2 12 8 18" />
              </svg>
              <div class="btn-text-block">
                <strong>Download Standalone HTML</strong>
                <span>Full offline interactive portfolio • Single .html file</span>
              </div>
              <span class="modal-btn-badge">OFFLINE • HTML</span>
            </button>
          </div>
        </div>

        <div class="modal-offline-status" role="status" style="display: none; margin-top: 10px; font-size: 12px; padding: 7px 12px; border-radius: 4px; background: var(--teal-bg); border: 1px solid var(--teal-border); color: var(--teal-bright); text-align: center;"></div>
      </section>

      <div class="modal-section-divider">
        <span>Direct Profile &amp; Verification</span>
      </div>

      <div class="modal-channels-container">
        <div class="modal-channels-grid">
          <a href="${linkedinUrl}" target="_blank" rel="noopener noreferrer" class="modal-channel-card linkedin-card" aria-label="Connect on LinkedIn">
            <div class="channel-icon-wrap">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
              </svg>
            </div>
            <div class="channel-text-block">
              <strong>Connect on LinkedIn</strong>
              <span>in/${linkedinVanity}</span>
            </div>
            <span class="channel-external-arrow" aria-hidden="true">↗</span>
          </a>

          <a href="${githubUrl}" target="_blank" rel="noopener noreferrer" class="modal-channel-card github-card" aria-label="Explore Source Code &amp; Repositories">
            <div class="channel-icon-wrap">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.53 1.032 1.53 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/>
              </svg>
            </div>
            <div class="channel-text-block">
              <strong>Explore Repositories</strong>
              <span>github.com/nagendradixit647</span>
            </div>
            <span class="channel-external-arrow" aria-hidden="true">↗</span>
          </a>
        </div>

        <button type="button" class="modal-copy-bar" aria-label="Copy LinkedIn Profile URL">
          <div class="copy-bar-left">
            <svg class="copy-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
            <div class="copy-bar-text">
              <strong>Copy LinkedIn Profile URL</strong>
              <span class="copy-bar-url">${linkedinUrl}</span>
            </div>
          </div>
          <span class="copy-badge">CLICK TO COPY</span>
        </button>
      </div>
    </div>

    <div class="modal-footer">
      <span class="modal-footer-hint">Vector PDF &amp; Standalone HTML • Privacy Safe</span>
      <button type="button" class="btn-modal-dismiss">Close Window</button>
    </div>
  </div>
</div>`;
}

/**
 * Generates the clean offline-ready mobile drawer markup with zero external dependencies.
 */
export function getStandaloneMobileDrawerHtml(): string {
  return `<div class="mobile-nav-overlay" data-testid="mobile-nav-overlay" style="display: none;" aria-hidden="true">
  <div id="mobile-nav-drawer" class="mobile-nav-drawer" role="dialog" aria-label="Mobile Navigation Menu" data-testid="mobile-nav-drawer" onclick="event.stopPropagation()">
    <div class="mobile-drawer-header">
      <span class="mobile-drawer-title">Navigation</span>
      <button type="button" class="mobile-drawer-close-btn" aria-label="Close navigation menu">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
    <div class="mobile-nav-links">
      <a href="#flagship" class="mobile-nav-link"><span>AI Flagship</span><span class="mobile-nav-arrow" aria-hidden="true">→</span></a>
      <a href="#featured" class="mobile-nav-link"><span>Frameworks</span><span class="mobile-nav-arrow" aria-hidden="true">→</span></a>
      <a href="#portfolio" class="mobile-nav-link"><span>Automation Wins</span><span class="mobile-nav-arrow" aria-hidden="true">→</span></a>
      <a href="#experience" class="mobile-nav-link"><span>Experience</span><span class="mobile-nav-arrow" aria-hidden="true">→</span></a>
      <a href="#skills" class="mobile-nav-link"><span>Skills</span><span class="mobile-nav-arrow" aria-hidden="true">→</span></a>
      <a href="#contact" class="mobile-nav-link btn"><span>Contact</span><span class="mobile-nav-arrow" aria-hidden="true">→</span></a>
      <button type="button" class="mobile-resume-btn" aria-label="Request CV / Résumé">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        <span>Request Full Résumé</span>
      </button>
    </div>
  </div>
</div>`;
}

export const DEFAULT_FALLBACK_CSS = `
:root, [data-theme="dark"] {
  --bg: #090F19;
  --bg-surface: #0E1726;
  --panel: #131F33;
  --panel-card: #142137;
  --text: #F8FAFC;
  --text-dim: #94A3B8;
  --text-faint: #8494A7;
  --teal: #14B8A6;
  --teal-bright: #2DD4BF;
  --amber: #F59E0B;
  --amber-bright: #FBBF24;
  --line: rgba(255, 255, 255, 0.08);
}
[data-theme="light"] {
  --bg: #F8FAFC;
  --bg-surface: #FFFFFF;
  --panel: #F1F5F9;
  --panel-card: #FFFFFF;
  --text: #0F172A;
  --text-dim: #475569;
  --text-faint: #64748B;
  --teal: #0D9488;
  --teal-bright: #0F766E;
  --amber: #B45309;
  --amber-bright: #D97706;
  --line: rgba(0, 0, 0, 0.08);
}
`;

/**
 * Extracts active stylesheet rules from document.styleSheets if accessible.
 */
export function getDocumentStyleSheetRules(): string {
  let css = '';
  if (typeof document === 'undefined') {
    return css;
  }
  try {
    const sheets = document.styleSheets;
    if (sheets) {
      for (let i = 0; i < sheets.length; i++) {
        const sheet = sheets[i];
        try {
          if (sheet.cssRules) {
            for (let j = 0; j < sheet.cssRules.length; j++) {
              css += sheet.cssRules[j].cssText + '\n';
            }
          }
        } catch {
          // Cross-origin stylesheet rules are ignored safely
        }
      }
    }
  } catch {
    // Fallback if styleSheets property access fails
  }
  return css;
}

/**
 * Returns a lightweight vanilla JavaScript controller script that embeds into
 * the exported offline HTML document.
 * Provides offline interactive state for:
 * - 5-stage architecture pipeline (tab clicks, SVG node clicks, mobile steps, panel updates)
 * - Dark/light mode theme toggle switch
 * - Mobile slide-over navigation drawer open/close
 * - Smooth anchor link scrolling
 * - Header "Request CV" fallback smooth-scroll to contact section
 */
export function getOfflineVanillaScript(): string {
  const stagesJson = JSON.stringify(flagshipProject.pipelineStages);
  return `(function() {
  var stages = ${stagesJson};
  var pillBtns = document.querySelectorAll('.stage-pill-btn');
  var svgNodes = document.querySelectorAll('.pipeline-node-g');
  var mobileSteps = document.querySelectorAll('.mobile-flow-step');
  var inspector = document.querySelector('.stage-inspector');

  function updatePipeline(idx) {
    if (idx < 0 || idx >= stages.length) return;
    var stg = stages[idx];

    pillBtns.forEach(function(btn, i) {
      if (i === idx) {
        btn.classList.add('active');
        btn.setAttribute('aria-selected', 'true');
      } else {
        btn.classList.remove('active');
        btn.setAttribute('aria-selected', 'false');
      }
    });

    svgNodes.forEach(function(node, i) {
      var rect = node.querySelector('.node-rect');
      var dot = node.querySelector('.node-status-dot');
      if (i === idx) {
        node.classList.add('selected');
        if (rect) rect.classList.add('active-rect');
        if (dot) dot.classList.add('active-dot');
      } else {
        node.classList.remove('selected');
        if (rect) rect.classList.remove('active-rect');
        if (dot) dot.classList.remove('active-dot');
      }
    });

    mobileSteps.forEach(function(step, i) {
      if (i === idx) {
        step.classList.add('active');
        step.setAttribute('aria-pressed', 'true');
      } else {
        step.classList.remove('active');
        step.setAttribute('aria-pressed', 'false');
      }
    });

    if (inspector) {
      inspector.id = 'stage-panel-' + stg.id;
      inspector.setAttribute('aria-labelledby', 'stage-tab-' + stg.id);
      
      var numEl = inspector.querySelector('.step-tag-num');
      if (numEl) numEl.textContent = 'STAGE ' + stg.step + ' OF 05';
      
      var titleEl = inspector.querySelector('.step-tag-title');
      if (titleEl) titleEl.textContent = stg.title;
      
      var techEl = inspector.querySelector('.inspector-tech-badge code');
      if (techEl) techEl.textContent = stg.tech;
      
      var descEl = inspector.querySelector('.inspector-desc');
      if (descEl) descEl.textContent = stg.fullDesc;
      
      var highlightsList = inspector.querySelector('.highlights-list');
      if (highlightsList) {
        highlightsList.innerHTML = '';
        stg.highlights.forEach(function(hl) {
          var li = document.createElement('li');
          li.innerHTML = '<svg class="check-icon" width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M16.6666 5L7.49992 14.1667L3.33325 10" stroke="var(--teal-bright)" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg><span>' + hl + '</span>';
          highlightsList.appendChild(li);
        });
      }
    }
  }

  pillBtns.forEach(function(btn, i) {
    btn.addEventListener('click', function() { updatePipeline(i); });
  });

  svgNodes.forEach(function(node, i) {
    node.addEventListener('click', function() { updatePipeline(i); });
    node.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        updatePipeline(i);
      }
    });
  });

  mobileSteps.forEach(function(step, i) {
    step.addEventListener('click', function() { updatePipeline(i); });
    step.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        updatePipeline(i);
      }
    });
  });

  var themeToggleBtn = document.querySelector('.theme-toggle-btn');
  if (themeToggleBtn) {
    themeToggleBtn.addEventListener('click', function() {
      var current = document.documentElement.getAttribute('data-theme') || 'dark';
      var next = current === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      document.documentElement.style.colorScheme = next;
      themeToggleBtn.setAttribute('aria-checked', next === 'dark' ? 'true' : 'false');
      try { localStorage.setItem('theme_preference', next); } catch(e) {}
      var metaTheme = document.querySelector('meta[name="theme-color"]');
      if (metaTheme) metaTheme.setAttribute('content', next === 'dark' ? '#090F19' : '#F8FAFC');
    });
  }

  var mobileToggle = document.querySelector('.mobile-menu-btn, .mobile-nav-toggle');
  var mobileDrawer = document.getElementById('mobile-nav-drawer');
  var drawerClose = document.querySelector('.mobile-drawer-close-btn');
  var drawerOverlay = document.querySelector('.mobile-nav-overlay, .mobile-drawer-overlay');
  var drawerLinks = document.querySelectorAll('.mobile-nav-links a, .mobile-drawer-links a');

  function openDrawer() {
    if (drawerOverlay) {
      drawerOverlay.style.display = 'flex';
      drawerOverlay.setAttribute('aria-hidden', 'false');
    }
    if (mobileDrawer) {
      mobileDrawer.classList.add('open');
      mobileDrawer.setAttribute('aria-hidden', 'false');
    }
    if (mobileToggle) mobileToggle.setAttribute('aria-expanded', 'true');
  }

  function closeDrawer() {
    if (drawerOverlay) {
      drawerOverlay.style.display = 'none';
      drawerOverlay.setAttribute('aria-hidden', 'true');
    }
    if (mobileDrawer) {
      mobileDrawer.classList.remove('open');
      mobileDrawer.setAttribute('aria-hidden', 'true');
    }
    if (mobileToggle) mobileToggle.setAttribute('aria-expanded', 'false');
  }

  if (mobileToggle) {
    mobileToggle.addEventListener('click', function() {
      var isDrawerOpen = drawerOverlay ? drawerOverlay.style.display === 'flex' : (mobileDrawer && mobileDrawer.classList.contains('open'));
      if (isDrawerOpen) {
        closeDrawer();
      } else {
        openDrawer();
      }
    });
  }
  if (drawerClose) drawerClose.addEventListener('click', closeDrawer);
  if (drawerOverlay) {
    drawerOverlay.addEventListener('click', function(e) {
      if (e.target === drawerOverlay) closeDrawer();
    });
  }
  if (mobileDrawer) {
    mobileDrawer.addEventListener('click', function(e) {
      e.stopPropagation();
    });
  }
  drawerLinks.forEach(function(link) { link.addEventListener('click', closeDrawer); });

  document.querySelectorAll('a[href^="#"]').forEach(function(anchor) {
    anchor.addEventListener('click', function(e) {
      var targetId = anchor.getAttribute('href');
      if (targetId === '#' || targetId === '#top') {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      var targetEl = document.querySelector(targetId);
      if (targetEl) {
        e.preventDefault();
        targetEl.scrollIntoView({ behavior: 'smooth' });
      }
    });
  });

  var modal = document.querySelector('.modal-overlay');
  var resumeBtns = document.querySelectorAll(
    '.nav-resume-btn, .mobile-resume-btn, .mobile-drawer-resume-btn, .btn-resume-card, [aria-label="Request Full Résumé"], [data-open-resume-modal]'
  );

  function openResumeModal() {
    if (!modal) return;
    if (mobileDrawer && mobileDrawer.classList.contains('open')) {
      closeDrawer();
    }
    modal.style.display = 'flex';
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    var closeBtn = modal.querySelector('.modal-close-btn');
    if (closeBtn) closeBtn.focus();
  }

  function closeResumeModal() {
    if (!modal) return;
    modal.style.display = 'none';
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  resumeBtns.forEach(function(btn) {
    btn.addEventListener('click', function(e) {
      e.preventDefault();
      openResumeModal();
    });
  });

  if (modal) {
    var closeBtn = modal.querySelector('.modal-close-btn');
    var dismissBtn = modal.querySelector('.btn-modal-dismiss');
    if (closeBtn) closeBtn.addEventListener('click', closeResumeModal);
    if (dismissBtn) dismissBtn.addEventListener('click', closeResumeModal);

    modal.addEventListener('click', function(e) {
      if (e.target === modal) {
        closeResumeModal();
      }
    });

    var modalContent = modal.querySelector('.modal-content');
    if (modalContent) {
      modalContent.addEventListener('click', function(e) {
        e.stopPropagation();
      });
    }

    window.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal.style.display === 'flex') {
        closeResumeModal();
      }
    });

    var copyBar = modal.querySelector('.modal-copy-bar');
    if (copyBar) {
      copyBar.addEventListener('click', function() {
        var copyUrl = ${JSON.stringify(SITE_CONFIG.defaultLinkedinUrl)};
        var badge = copyBar.querySelector('.copy-badge');
        var title = copyBar.querySelector('.copy-bar-text strong');
        var desc = copyBar.querySelector('.copy-bar-url');

        function setCopiedSuccess() {
          copyBar.classList.add('copied');
          if (badge) badge.textContent = 'COPIED';
          if (title) title.textContent = 'Copied to Clipboard!';
          if (desc) desc.textContent = 'Ready to paste into your browser or messenger';
          setTimeout(function() {
            copyBar.classList.remove('copied');
            if (badge) badge.textContent = 'CLICK TO COPY';
            if (title) title.textContent = 'Copy LinkedIn Profile URL';
            if (desc) desc.textContent = copyUrl;
          }, 2500);
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(copyUrl).then(setCopiedSuccess).catch(function() {
            tryFallbackCopy();
          });
        } else {
          tryFallbackCopy();
        }

        function tryFallbackCopy() {
          try {
            var ta = document.createElement('textarea');
            ta.value = copyUrl;
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            var success = document.execCommand('copy');
            document.body.removeChild(ta);
            if (success) {
              setCopiedSuccess();
            }
          } catch(e) {}
        }
      });
    }

    var orientSelect = modal.querySelector('#pdf-orientation-select');
    var themeSelect = modal.querySelector('#pdf-theme-select');
    var pdfBtn = modal.querySelector('.modal-action-btn.primary');
    var htmlBtn = modal.querySelector('.modal-action-btn.html-export-btn');
    var statusEl = modal.querySelector('.modal-offline-status');

    function updatePdfDetails() {
      if (!pdfBtn) return;
      var orient = orientSelect ? orientSelect.value : 'portrait';
      var theme = themeSelect ? themeSelect.value : 'light';
      var sub = pdfBtn.querySelector('.btn-text-block span');
      var badge = pdfBtn.querySelector('.modal-btn-badge');
      if (sub) {
        sub.textContent = (orient === 'portrait' ? 'A4 Executive CV' : 'A4 Presentation View') + ' • ' + (theme === 'light' ? 'Light Theme' : 'Dark Theme');
      }
      if (badge) {
        badge.textContent = orient === 'portrait' ? 'A4 • PORTRAIT' : 'A4 • LANDSCAPE';
      }
    }

    if (orientSelect) orientSelect.addEventListener('change', updatePdfDetails);
    if (themeSelect) themeSelect.addEventListener('change', updatePdfDetails);

    if (pdfBtn) {
      pdfBtn.addEventListener('click', function() {
        if (statusEl) {
          statusEl.style.display = 'block';
          statusEl.textContent = 'Opening print & save-as-PDF dialog...';
        }
        setTimeout(function() {
          window.print();
        }, 300);
      });
    }

    if (htmlBtn) {
      htmlBtn.addEventListener('click', function() {
        try {
          var html = '<!DOCTYPE html>\\n' + document.documentElement.outerHTML;
          var blob = new Blob([html], { type: 'text/html;charset=utf-8' });
          var blobUrl = URL.createObjectURL(blob);
          var a = document.createElement('a');
          a.href = blobUrl;
          a.download = 'Nagendra_Dixit_Portfolio.html';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(blobUrl);
          if (statusEl) {
            statusEl.style.display = 'block';
            statusEl.textContent = 'Standalone offline HTML portfolio downloaded successfully.';
          }
        } catch (e) {
          if (statusEl) {
            statusEl.style.display = 'block';
            statusEl.textContent = 'Error downloading offline file.';
          }
        }
      });
    }
  }
})();`;
}

/**
 * Extracts and compiles the current portfolio DOM into a complete, standalone,
 * single-file HTML document with inlined CSS and vanilla interactivity.
 */
export function generateStandaloneHtml(options: StandaloneHtmlOptions = {}): string {
  const activeTheme: ResolvedTheme =
    options.activeTheme ||
    (typeof document !== 'undefined' && (document.documentElement.getAttribute('data-theme') as ResolvedTheme)) ||
    'dark';

  const title =
    options.documentTitle ||
    'Nagendra Vikram Dixit | AI Agent & Senior Backend Engineer (Offline Snapshot)';

  let rootHtml = '';
  if (typeof document !== 'undefined') {
    const rootElement = document.getElementById('root') || document.body;
    const clone = rootElement.cloneNode(true) as HTMLElement;

    // Strip any existing active mobile drawers/overlays from clone
    const existingDrawers = clone.querySelectorAll('.mobile-nav-overlay, .mobile-nav-drawer');
    existingDrawers.forEach((d) => d.remove());

    // Inject clean offline-ready mobile drawer into clone (hidden by default)
    const drawerWrapper = document.createElement('div');
    drawerWrapper.innerHTML = getStandaloneMobileDrawerHtml().trim();
    if (drawerWrapper.firstElementChild) {
      clone.appendChild(drawerWrapper.firstElementChild);
    }

    // Strip any existing active modals from clone to ensure clean offline structure
    const existingModals = clone.querySelectorAll('.modal-overlay, [role="dialog"]');
    existingModals.forEach((m) => m.remove());

    // Inject the clean offline-ready modal into the clone (hidden by default)
    const modalWrapper = document.createElement('div');
    modalWrapper.innerHTML = getStandaloneModalHtml().trim();
    if (modalWrapper.firstElementChild) {
      clone.appendChild(modalWrapper.firstElementChild);
    }

    rootHtml = clone.innerHTML;
  } else {
    rootHtml = `${getStandaloneMobileDrawerHtml().trim()}\n${getStandaloneModalHtml().trim()}`;
  }

  const capturedCss = getDocumentStyleSheetRules();
  const rawImportsCss = `${indexCss || ''}\n${appCss || ''}`.trim();
  const fallbackCss = !rawImportsCss && !capturedCss ? DEFAULT_FALLBACK_CSS.trim() : '';
  const inlinedCss = [
    rawImportsCss,
    capturedCss,
    fallbackCss,
    'html { scroll-behavior: smooth; }',
    '@media print { .modal-overlay, .mobile-nav-toggle, .mobile-nav-drawer, .theme-toggle-btn { display: none !important; } }',
  ]
    .filter(Boolean)
    .join('\n');

  const vanillaScript = getOfflineVanillaScript();

  return `<!DOCTYPE html>
<html lang="en" data-theme="${activeTheme}" style="color-scheme: ${activeTheme}">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${title}</title>
  <meta name="description" content="Nagendra Vikram Dixit — Enterprise AI Agent Builder &amp; Senior Backend Engineer. Standalone offline HTML snapshot." />
  <meta name="theme-color" content="${activeTheme === 'dark' ? '#090F19' : '#F8FAFC'}" />
  <style>
${inlinedCss}
  </style>
</head>
<body>
  <div id="root">
${rootHtml}
  </div>
  <script>
${vanillaScript}
  </script>
</body>
</html>`;
}

/**
 * Triggers a client-side Blob download of the standalone single-file HTML portfolio.
 */
export async function exportStandaloneHtml(options: StandaloneHtmlOptions = {}): Promise<void> {
  const filename = options.filename || 'Nagendra_Dixit_Portfolio.html';
  const htmlContent = generateStandaloneHtml(options);

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
